import { Modal, Tag, Tooltip, Input, Select, Progress } from "antd";
import { Plus, Star, X, Search, Filter, Calendar, Clock, Truck } from "lucide-react";
import { FC, useState, useMemo } from "react";
import dayjs, { Dayjs } from "dayjs";
import { useGetAllStaff } from "~/services/operation";
import { useGetStaffAvailabilities } from "~/services/staff/staff-availability.queries";
import { useGetVesselsForDropdown } from "~/services/vessel/vessel.queries";
import { useBooking } from "~/store";
import { StaffAvailability, StaffStatus } from "~/utils/constants";
import { cn } from "~/utils";
import { TStaff } from "~/types";

type Props = {
  value?: string[];
  onChange?: (value: string[]) => void;
  serviceDate?: Dayjs | null; // The date of the service
};

export const StaffAssignment: FC<Props> = ({ value, onChange, serviceDate }) => {
  const [open, setOpen] = useState(false);

  // Filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [filterLead, setFilterLead] = useState<boolean | null>(null);
  const [filterDriver, setFilterDriver] = useState<boolean | null>(null);
  const [filterMinRating, setFilterMinRating] = useState<number | null>(null);
  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  const [filterAvailability, setFilterAvailability] = useState<string | null>("AVAILABLE");

  const { viewMode } = useBooking();
  const { data: staffs } = useGetAllStaff();
  const { data: vessels } = useGetVesselsForDropdown();

  // Map staff to their vessel assignments
  const staffVesselMap = useMemo(() => {
    const map = new Map<string, { vesselName: string; vesselId: string; roles: string[] }[]>();

    vessels?.forEach((vessel) => {
      vessel.vesselStaffs?.forEach((vs) => {
        const existing = map.get(vs.staffId) || [];
        existing.push({
          vesselName: vessel.name,
          vesselId: vessel.id,
          roles: vs.roles || [],
        });
        map.set(vs.staffId, existing);
      });
    });

    return map;
  }, [vessels]);

  // Fetch availability for the service date
  const serviceDateStr = serviceDate ? serviceDate.format("YYYY-MM-DD") : null;
  const { data: availabilityData } = useGetStaffAvailabilities(
    {
      startDate: serviceDateStr || undefined,
      endDate: serviceDateStr || undefined,
      pageSize: 500,
    },
    !!serviceDateStr
  );

  // Map availability by staff ID
  const availabilityByStaff = useMemo(() => {
    const map = new Map<string, { availability: StaffAvailability; bookedHours: number; availableHours: number; note?: string }>();
    availabilityData?.items?.forEach((schedule) => {
      map.set(schedule.staffId, {
        availability: schedule.availability,
        bookedHours: schedule.bookedHours || 0,
        availableHours: schedule.availableHours || 8,
        note: schedule.note,
      });
    });
    return map;
  }, [availabilityData?.items]);

  // Calculate average rating for a staff member
  const calculateAverageRating = (staff: TStaff) => {
    const ratings = [
      staff.installation || 0,
      staff.packing || 0,
      staff.handling || 0,
      staff.clientRelations || 0,
      staff.teamwork || 0,
    ];
    const validRatings = ratings.filter((r) => r > 0);
    if (validRatings.length === 0) return 0;
    return validRatings.reduce((sum, r) => sum + r, 0) / validRatings.length;
  };

  // Filter staff based on all criteria including availability
  const filteredStaff = useMemo(() => {
    if (!staffs?.items) return [];

    return staffs.items.filter((staff) => {
      // Search filter - name and email
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const fullName = `${staff.user?.firstName || ""} ${staff.user?.lastName || ""}`.toLowerCase();
        const email = staff.user?.email?.toLowerCase() || "";
        if (!fullName.includes(query) && !email.includes(query)) return false;
      }

      // Lead filter
      if (filterLead !== null && staff.lead !== filterLead) return false;

      // Driver filter
      if (filterDriver !== null && staff.driver !== filterDriver) return false;

      // Rating filter
      if (filterMinRating !== null) {
        const avgRating = calculateAverageRating(staff);
        if (avgRating < filterMinRating) return false;
      }

      // Status filter
      if (filterStatus !== null && staff.status !== filterStatus) return false;

      // Availability filter (only if we have a service date)
      if (filterAvailability !== null && serviceDateStr) {
        const availability = availabilityByStaff.get(staff.id);
        if (filterAvailability === "AVAILABLE") {
          // Only show FULL_TIME or PART_TIME
          if (!availability || availability.availability === StaffAvailability.UNAVAILABLE) {
            return false;
          }
        } else if (filterAvailability === "HAS_CAPACITY") {
          // Only show staff with > 2 hours available
          if (!availability || availability.availableHours < 2) {
            return false;
          }
        }
      }

      return true;
    });
  }, [staffs?.items, searchQuery, filterLead, filterDriver, filterMinRating, filterStatus, filterAvailability, serviceDateStr, availabilityByStaff]);

  // Sort staff: available first, then by available hours
  const sortedStaff = useMemo(() => {
    if (!serviceDateStr) return filteredStaff;

    return [...filteredStaff].sort((a, b) => {
      const availA = availabilityByStaff.get(a.id);
      const availB = availabilityByStaff.get(b.id);

      // Sort by availability status first
      const statusOrder = { FULL_TIME: 0, PART_TIME: 1, UNAVAILABLE: 2 };
      const statusA = availA?.availability || "FULL_TIME";
      const statusB = availB?.availability || "FULL_TIME";

      if (statusOrder[statusA] !== statusOrder[statusB]) {
        return statusOrder[statusA] - statusOrder[statusB];
      }

      // Then by available hours (descending)
      const hoursA = availA?.availableHours ?? 8;
      const hoursB = availB?.availableHours ?? 8;
      return hoursB - hoursA;
    });
  }, [filteredStaff, serviceDateStr, availabilityByStaff]);

  // Get unique statuses from staff data for the filter dropdown
  const availableStatuses = useMemo(() => {
    if (!staffs?.items) return [];
    const statuses = new Set(staffs.items.map((s) => s.status).filter(Boolean));
    return Array.from(statuses);
  }, [staffs?.items]);

  // Reset filters when modal closes
  const handleCloseModal = () => {
    setOpen(false);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setFilterLead(null);
    setFilterDriver(null);
    setFilterMinRating(null);
    setFilterStatus(null);
    setFilterAvailability(null);
  };

  const hasActiveFilters = searchQuery || filterLead !== null || filterDriver !== null || filterMinRating !== null || filterStatus !== null || filterAvailability !== null;

  const handleRemoveStaff = (staffId: string) => {
    if (value) {
      const updatedStaffs = value.filter((staff) => staff !== staffId);
      onChange?.(updatedStaffs);
    }
  };

  const getAvailabilityTag = (staff: TStaff) => {
    if (!serviceDateStr) {
      // Fall back to general availability
      switch (staff.availability) {
        case StaffAvailability.FULL_TIME:
          return <Tag color="green">Available</Tag>;
        case StaffAvailability.PART_TIME:
          return <Tag color="orange">Part-time</Tag>;
        case StaffAvailability.UNAVAILABLE:
          return <Tag color="red">Unavailable</Tag>;
        default:
          return <Tag color="default">Unknown</Tag>;
      }
    }

    const schedule = availabilityByStaff.get(staff.id);
    if (!schedule) {
      return <Tag color="default">Pending</Tag>;
    }

    switch (schedule.availability) {
      case StaffAvailability.FULL_TIME:
        return <Tag color="green">Full Day</Tag>;
      case StaffAvailability.PART_TIME:
        return <Tag color="orange">Half Day</Tag>;
      case StaffAvailability.UNAVAILABLE:
        return <Tag color="red">Unavailable</Tag>;
      default:
        return <Tag color="default">Unknown</Tag>;
    }
  };

  const getHoursIndicator = (staff: TStaff) => {
    if (!serviceDateStr) return null;

    const schedule = availabilityByStaff.get(staff.id);
    if (!schedule) return null;

    const bookedHours = schedule.bookedHours || 0;
    const availableHours = schedule.availableHours || 8;
    const percentUsed = Math.round((bookedHours / 8) * 100);

    let color = "green";
    if (percentUsed >= 75) color = "red";
    else if (percentUsed >= 50) color = "orange";

    return (
      <Tooltip title={`${bookedHours}h booked / ${availableHours}h available`}>
        <div className="flex items-center gap-1">
          <Progress
            percent={percentUsed}
            size="small"
            showInfo={false}
            strokeColor={color}
            trailColor="#e5e7eb"
            style={{ width: 50 }}
          />
          <span className="text-[10px] text-gray-500">{availableHours}h</span>
        </div>
      </Tooltip>
    );
  };

  const getRoleIndicators = (staff: any) => {
    const indicators = [];

    if (staff.lead) {
      indicators.push(
        <Tooltip key="lead" title="Lead">
          <Tag color="blue">L</Tag>
        </Tooltip>
      );
    }

    if (staff.driver) {
      indicators.push(
        <Tooltip key="driver" title="Driver">
          <Tag color="purple">D</Tag>
        </Tooltip>
      );
    }

    return indicators;
  };

  const getVesselBadges = (staffId: string) => {
    const vesselAssignments = staffVesselMap.get(staffId);
    if (!vesselAssignments || vesselAssignments.length === 0) return null;

    return (
      <div className="flex items-center gap-1 flex-wrap">
        {vesselAssignments.map((va) => (
          <Tooltip
            key={va.vesselId}
            title={`Assigned to ${va.vesselName}${va.roles.length > 0 ? ` as ${va.roles.join(", ")}` : ""}`}
          >
            <Tag
              color="cyan"
              className="flex items-center gap-0.5 text-[10px] px-1 py-0"
              style={{ margin: 0 }}
            >
              <Truck size={10} />
              {va.vesselName}
            </Tag>
          </Tooltip>
        ))}
      </div>
    );
  };

  // Helper function to get staff display name
  const getStaffDisplayName = (staff: any) => {
    if (!staff?.user) return 'Staff (No User)';

    const firstName = staff.user.firstName || '';
    const lastName = staff.user.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();

    return fullName || staff.user.username || 'Unknown Staff';
  };

  // Helper function to get staff initials
  const getStaffInitials = (staff: any) => {
    if (!staff?.user) return staff.id.slice(0, 2).toUpperCase();

    const firstName = staff.user.firstName || '';
    const lastName = staff.user.lastName || '';

    if (firstName || lastName) {
      return `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase();
    }

    return staff.user.username?.slice(0, 2).toUpperCase() || staff.id.slice(0, 2).toUpperCase();
  };

  const viewOnly = viewMode === "view";

  return (
    <div
      className={cn(
        "flex flex-wrap gap-2 px-2 items-center border rounded-lg py-1.5",
        viewOnly &&
          "text-[rgba(0,0,0,0.25)] bg-[rgba(0,0,0,0.04)] border-[#d9d9d9] cursor-not-allowed"
      )}
    >
      {value?.map((staff) => {
        const staffData = staffs?.items.find((s) => s.id === staff);
        return (
          <div
            key={staff}
            className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs flex items-center"
          >
            {staffData ? getStaffDisplayName(staffData) : "Staff"}
            {!viewOnly && (
              <X
                size={14}
                className="ml-1 cursor-pointer"
                onClick={() => handleRemoveStaff(staff)}
              />
            )}
          </div>
        );
      }) ?? null}
      <button
        type="button"
        className={cn(
          "px-2 py-1 border rounded-full text-xs flex items-center hover:bg-gray-50 cursor-pointer",
          viewOnly && "cursor-not-allowed"
        )}
        onClick={() => !viewOnly && setOpen(true)}
      >
        <Plus size={14} className="mr-1" /> Add Staff
      </button>
      <Modal
        title={
          <div className="flex items-center justify-between pr-8">
            <div className="flex items-center gap-2">
              <span>Select Staff</span>
              {serviceDateStr && (
                <Tag color="blue" className="flex items-center gap-1">
                  <Calendar size={12} />
                  {dayjs(serviceDateStr).format("MMM D, YYYY")}
                </Tag>
              )}
            </div>
            <span className="text-sm font-normal text-gray-500">
              {sortedStaff.length} of {staffs?.items?.length || 0} staff
            </span>
          </div>
        }
        open={open}
        onCancel={handleCloseModal}
        footer={null}
        width={750}
      >
        {/* Service date notice */}
        {!serviceDateStr && (
          <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800 flex items-center gap-2">
            <Clock size={16} />
            Select a service date first to see staff availability for that day
          </div>
        )}

        {/* Filters Section */}
        <div className="mb-4 space-y-3">
          {/* Search */}
          <Input
            placeholder="Search by name or email..."
            prefix={<Search size={16} className="text-gray-400" />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            allowClear
          />

          {/* Filter Row */}
          <div className="flex flex-wrap gap-2 items-center">
            <Filter size={16} className="text-gray-400" />

            {/* Availability Filter (only when date selected) */}
            {serviceDateStr && (
              <Select
                placeholder="Availability"
                value={filterAvailability}
                onChange={setFilterAvailability}
                allowClear
                style={{ width: 130 }}
                size="small"
                options={[
                  { label: "Available", value: "AVAILABLE" },
                  { label: "Has Capacity", value: "HAS_CAPACITY" },
                ]}
              />
            )}

            {/* Lead Filter */}
            <Select
              placeholder="Lead"
              value={filterLead}
              onChange={setFilterLead}
              allowClear
              style={{ width: 100 }}
              size="small"
              options={[
                { label: "Lead", value: true },
                { label: "Not Lead", value: false },
              ]}
            />

            {/* Driver Filter */}
            <Select
              placeholder="Driver"
              value={filterDriver}
              onChange={setFilterDriver}
              allowClear
              style={{ width: 110 }}
              size="small"
              options={[
                { label: "Driver", value: true },
                { label: "Not Driver", value: false },
              ]}
            />

            {/* Rating Filter */}
            <Select
              placeholder="Min Rating"
              value={filterMinRating}
              onChange={setFilterMinRating}
              allowClear
              style={{ width: 110 }}
              size="small"
              options={[
                { label: "1+ ★", value: 1 },
                { label: "2+ ★", value: 2 },
                { label: "3+ ★", value: 3 },
                { label: "4+ ★", value: 4 },
              ]}
            />

            {/* Status Filter */}
            <Select
              placeholder="Status"
              value={filterStatus}
              onChange={setFilterStatus}
              allowClear
              style={{ width: 120 }}
              size="small"
              options={availableStatuses.map((status) => ({
                label: status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, " "),
                value: status,
              }))}
            />

            {/* Clear Filters */}
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-xs text-blue-600 hover:text-blue-800 ml-2"
              >
                Clear all
              </button>
            )}
          </div>
        </div>

        {/* Staff List - Scrollable */}
        <div className="flex flex-col gap-2 max-h-[400px] overflow-y-auto pr-1">
          {sortedStaff.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No staff found matching your filters
            </div>
          ) : (
            sortedStaff.map((staff) => {
              const isSelected = value?.includes(staff.id);
              const rating = calculateAverageRating(staff);
              const schedule = serviceDateStr ? availabilityByStaff.get(staff.id) : null;
              const isUnavailable = schedule?.availability === StaffAvailability.UNAVAILABLE;

              return (
                <div
                  key={staff.id}
                  className={cn(
                    "flex items-center justify-between p-3 border rounded hover:bg-gray-50",
                    isSelected && "bg-blue-50 border-blue-200",
                    isUnavailable && "opacity-60 bg-gray-50"
                  )}
                >
                  <div className="flex items-center space-x-2">
                    <div className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center text-gray-600",
                      isUnavailable ? "bg-gray-300" : "bg-gray-200"
                    )}>
                      {getStaffInitials(staff)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium flex items-center gap-2 flex-wrap">
                        {getStaffDisplayName(staff)}
                        <span className="inline-flex gap-1">
                          {getRoleIndicators(staff)}
                        </span>
                        {getVesselBadges(staff.id)}
                      </div>
                      <div className="flex items-center text-xs text-gray-500 gap-2">
                        <span className="flex items-center">
                          <Star
                            size={12}
                            fill="gold"
                            color="gold"
                            className="mr-0.5"
                          />
                          {rating.toFixed(1)}
                        </span>
                        <span>{staff.staffRole?.name || "Staff"}</span>
                        {schedule?.note && (
                          <Tooltip title={schedule.note}>
                            <span className="text-orange-500 italic truncate max-w-[100px]">
                              "{schedule.note}"
                            </span>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getHoursIndicator(staff)}
                    {getAvailabilityTag(staff)}
                    <button
                      type="button"
                      className={cn(
                        "px-3 py-1 border rounded-full text-xs flex items-center cursor-pointer",
                        isSelected
                          ? "bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-200"
                          : "hover:bg-gray-100",
                        isUnavailable && !isSelected && "cursor-not-allowed opacity-50"
                      )}
                      onClick={() => {
                        if (isSelected) {
                          handleRemoveStaff(staff.id);
                        } else {
                          onChange?.([...(value ?? []), staff.id]);
                        }
                      }}
                      disabled={isUnavailable && !isSelected}
                    >
                      {isSelected ? "Deselect" : "Select"}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Modal>
    </div>
  );
};
